import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { usePagination } from 'hooks/usePagination'
import Pagination from 'features/Pagination'
import Table from 'components/Table'
import { FormattedMessage } from 'react-intl'
import {
  CRITERIA_STATUS_FILTER_ALL,
  SEARCH_DEBOUNCE_MS,
  TAXONOMIC_GROUP_FILTER_ALL,
  UNDER_ASSESSMENT_FILTER
} from 'utils/speciesCriteriaMet'
import {
  filterCalculationData,
  getCalculationSpeciesCounts,
  getCalculationToolbarSpecies,
  getNextSortState,
  sortCalculationRows,
  DEFAULT_SORT
} from 'utils/calculationSpeciesTable'
import { makeCalculationRow, makeCalculationsColumns } from './calculationTable'
import SpeciesTableToolbar from './speciesCriteriaMet/SpeciesTableToolbar'
import {
  SpeciesCriteriaMetContainer,
  SpeciesCriteriaMetShell,
  SpeciesPaginationSummary,
  SortableHeaderButton,
  SortIndicator
} from './speciesCriteriaMet/style'
import { SpeciesPaginationWrapper, StyledCalculationTable } from './style'
import { runCalculationsSafely } from './calculationsErrorHandling'

const PAGE_SIZE = 12

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: PAGE_SIZE,
  totalPages: 1,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const getSortStorageKey = (siteId, historyId) => {
  return `calculationsSpeciesSort:${siteId}:${historyId || 'current'}`
}

const readStoredSort = (siteId, historyId) => {
  try {
    const stored = sessionStorage.getItem(getSortStorageKey(siteId, historyId))
    return stored ? JSON.parse(stored) : DEFAULT_SORT
  } catch {
    return DEFAULT_SORT
  }
}

const CalculationsSpeciesTable = ({
  calculationData,
  isLoading,
  siteId,
  historyId,
  site,
  disabled,
  showUnderAssessmentFilter = false,
  canSeeSiteIBACriteria,
  shouldShowSiteIBAData
}) => {
  const [search, setSearch] = useState('')
  const [taxonomicGroup, setTaxonomicGroup] = useState(TAXONOMIC_GROUP_FILTER_ALL)
  const [criteriaStatus, setCriteriaStatus] = useState(CRITERIA_STATUS_FILTER_ALL)
  const [underAssessment, setUnderAssessment] = useState(UNDER_ASSESSMENT_FILTER.ALL)
  const [sortConfig, setSortConfig] = useState(() => readStoredSort(siteId, historyId))
  const {
    pagination,
    setPagination,
    selectPage,
    nextPage,
    prevPage,
    nextSection,
    prevSection
  } = usePagination(defaultPaginationState)

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)

  const toolbarSpecies = useMemo(() => {
    return runCalculationsSafely(
      'buildToolbarSpecies',
      () => getCalculationToolbarSpecies(calculationData),
      []
    )
  }, [calculationData])

  useEffect(() => {
    sessionStorage.setItem(getSortStorageKey(siteId, historyId), JSON.stringify(sortConfig))
  }, [sortConfig, siteId, historyId])

  const filterOptions = useMemo(() => ({
    search: debouncedSearch,
    taxonomicGroup,
    criteriaStatus,
    underAssessment
  }), [debouncedSearch, taxonomicGroup, criteriaStatus, underAssessment])

  const filteredCalculationData = useMemo(() => {
    return runCalculationsSafely(
      'filterCalculationData',
      () => filterCalculationData(calculationData, filterOptions),
      []
    )
  }, [calculationData, filterOptions])

  const tableRows = useMemo(() => {
    return runCalculationsSafely(
      'buildSpeciesTableRows',
      () => {
        const rows = makeCalculationRow(
          filteredCalculationData,
          site,
          disabled,
          canSeeSiteIBACriteria,
          shouldShowSiteIBAData
        )

        return sortCalculationRows(rows, sortConfig)
      },
      []
    )
  }, [
    filteredCalculationData,
    site,
    disabled,
    canSeeSiteIBACriteria,
    shouldShowSiteIBAData,
    sortConfig
  ])

  const speciesCounts = useMemo(() => {
    return runCalculationsSafely(
      'getSpeciesCounts',
      () => getCalculationSpeciesCounts(calculationData, filterOptions),
      { totalCount: 0, filteredCount: 0 }
    )
  }, [calculationData, filterOptions])

  useEffect(() => {
    setPagination((current) => ({ ...current, currentPage: 1 }))
  }, [debouncedSearch, taxonomicGroup, criteriaStatus, underAssessment, setPagination])

  const paginationMeta = useMemo(() => {
    const totalNumber = tableRows.length
    const totalPages = Math.max(1, Math.ceil(totalNumber / PAGE_SIZE))
    const currentPage = Math.min(pagination.currentPage, totalPages)
    const startIndex = totalNumber === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
    const endIndex = Math.min(currentPage * PAGE_SIZE, totalNumber)

    return { totalNumber, totalPages, currentPage, startIndex, endIndex }
  }, [tableRows.length, pagination.currentPage])

  const paginatedRows = useMemo(() => {
    const startIndex = (paginationMeta.currentPage - 1) * PAGE_SIZE

    return tableRows.slice(startIndex, startIndex + PAGE_SIZE)
  }, [tableRows, paginationMeta.currentPage])

  const hasActiveFilters = (
    debouncedSearch.trim() !== '' ||
    taxonomicGroup !== TAXONOMIC_GROUP_FILTER_ALL ||
    criteriaStatus !== CRITERIA_STATUS_FILTER_ALL ||
    (showUnderAssessmentFilter && underAssessment !== UNDER_ASSESSMENT_FILTER.ALL)
  )

  const handleClearFilters = useCallback(() => {
    setSearch('')
    setTaxonomicGroup(TAXONOMIC_GROUP_FILTER_ALL)
    setCriteriaStatus(CRITERIA_STATUS_FILTER_ALL)
    setUnderAssessment(UNDER_ASSESSMENT_FILTER.ALL)
    setSortConfig(DEFAULT_SORT)
  }, [])

  const handleSort = useCallback((columnId) => {
    setSortConfig((currentSort) => getNextSortState(currentSort, columnId))
  }, [])

  const sortOptions = useMemo(() => ({
    sortConfig,
    onSort: handleSort,
    SortableHeaderButton,
    SortIndicator
  }), [sortConfig, handleSort])

  const showIBAColumn = canSeeSiteIBACriteria(site)

  const columns = useMemo(() => {
    return makeCalculationsColumns(showIBAColumn, sortOptions)
  }, [showIBAColumn, sortOptions])

  const showPagination = paginationMeta.totalNumber > PAGE_SIZE

  return (
    <SpeciesCriteriaMetContainer>
      <SpeciesCriteriaMetShell>
        <SpeciesTableToolbar
          species={toolbarSpecies}
          search={search}
          onSearchChange={(event) => setSearch(event.target.value)}
          taxonomicGroup={taxonomicGroup}
          onTaxonomicGroupChange={setTaxonomicGroup}
          criteriaStatus={criteriaStatus}
          onCriteriaStatusChange={setCriteriaStatus}
          underAssessment={underAssessment}
          onUnderAssessmentChange={setUnderAssessment}
          filteredCount={speciesCounts.filteredCount}
          totalCount={speciesCounts.totalCount}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          showUnderAssessmentFilter={showUnderAssessmentFilter}
        />
        <StyledCalculationTable>
          <Table
            rows={paginatedRows}
            columns={columns}
            dataTest="calculations-species-table"
            noResultMessage={hasActiveFilters ? 'speciesCriteriaMetNoResults' : 'noSpeciesSelected'}
            noClearFilter={!hasActiveFilters}
            onClearFilter={hasActiveFilters ? handleClearFilters : undefined}
            isLoading={isLoading}
            overflow={false}
          />
        </StyledCalculationTable>
      </SpeciesCriteriaMetShell>
      {showPagination && (
        <SpeciesPaginationWrapper data-test="species-pagination-wrapper">
          <SpeciesPaginationSummary>
            <FormattedMessage id="showing" />
            {' '}
            {paginationMeta.startIndex}
            {' '}
            -
            {' '}
            {paginationMeta.endIndex}
            {' '}
            <FormattedMessage id="speciesCriteriaMetPageRangeOf" />
            {' '}
            {paginationMeta.totalNumber}
          </SpeciesPaginationSummary>
          {paginationMeta.totalPages > 1 && (
            <Pagination
              activePages={paginationMeta.currentPage}
              numberOfPages={paginationMeta.totalPages}
              nextPage={nextPage}
              prevPage={prevPage}
              nextSection={nextSection}
              prevSection={prevSection}
              selectPage={selectPage}
              dataTest="page-number"
            />
          )}
        </SpeciesPaginationWrapper>
      )}
    </SpeciesCriteriaMetContainer>
  )
}

CalculationsSpeciesTable.propTypes = {
  calculationData: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  historyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  showUnderAssessmentFilter: PropTypes.bool,
  canSeeSiteIBACriteria: PropTypes.func.isRequired,
  shouldShowSiteIBAData: PropTypes.func.isRequired
}

export default CalculationsSpeciesTable
